<template>
  <validation-observer ref="form" @submit.stop.prevent="() => {}" v-slot="{ invalid }">
    <modal-window>
      <h1 class="mb-4">Send as E-Mail</h1>
      <form>
        <div class="label mt-2">To E-Mail</div>
        <multiselect
          v-model="chosenRecipients"
          placeholder="Search for or enter recipient email"
          label="name"
          name="chosenRecipientsSelect"
          track-by="name"
          :options="options"
          :multiple="true"
          open-direction="bottom"
          :taggable="true"
          @tag="(tag) => addRecipient(tag, 'to')"
          :internal-search="false"
          :hide-selected="true"
          :options-limit="100"
          @search-change="searchReceivers"
          tagPlaceholder="Press enter to add the address"
          noOptions="Enter at least one character"
        >
          <template slot="tag" slot-scope="props">
            <div class="multiselect-tag">
              <div :title="props.option.email" v-b-tooltip:hover>
                <span v-if="props.option.name !== props.option.email"><i class="far fa-envelope"></i></span>
                {{ props.option.name }}
              </div>
              <i class="far fa-times cursor-pointer" @click="props.remove(props.option)"></i>
            </div>
          </template>
          <template slot="option" slot-scope="props">
            <span v-if="!props.option.isTag">
              <i class="far fa-envelope"></i>
              {{ props.option.name }}
            </span>
            <span v-else>
              {{ props.option.label }}
            </span>
          </template>
          <template slot="noOptions">
            <i class="far fa-search"></i>
            Enter at least one character
          </template>
        </multiselect>

        <div class="label mt-2">Cc E-Mail</div>
        <multiselect
          v-model="chosenRecipientsCc"
          placeholder="Search for or enter recipient email"
          label="name"
          track-by="name"
          :options="options"
          :multiple="true"
          open-direction="bottom"
          :taggable="true"
          @tag="(tag) => addRecipient(tag, 'cc')"
          :internal-search="false"
          :hide-selected="true"
          :options-limit="100"
          @search-change="searchReceivers"
          tagPlaceholder="Press enter to add the address"
          noOptions="Enter at least one character"
        >
          <template slot="tag" slot-scope="props">
            <div class="multiselect-tag">
              <div :title="props.option.email" v-b-tooltip:hover>
                <span v-if="props.option.name !== props.option.email"><i class="far fa-envelope"></i></span>
                {{ props.option.name }}
              </div>
              <i class="far fa-times cursor-pointer" @click="props.remove(props.option)"></i>
            </div>
          </template>
          <template slot="option" slot-scope="props">
            <span v-if="!props.option.isTag">
              <i class="far fa-envelope"></i>
              {{ props.option.name }}
            </span>
            <span v-else>
              {{ props.option.label }}
            </span>
          </template>
          <template slot="noOptions">
            <i class="far fa-search"></i>
            Enter at least one character
          </template>
        </multiselect>

        <hr />

        <ValidationProvider name="subject" rules="required" v-slot="{ errors }">
          <div class="form-group">
            <input
              type="text"
              id="subject"
              class="form-control form-control-sm"
              :class="{ 'is-invalid': !!errors[0] }"
              v-model="sendAsEmail.subject"
              placeholder="Subject of the E-Mail"
            />

            <div class="invalid-feedback">Subject is required</div>
          </div>
        </ValidationProvider>

        <div class="form-group">
          <wysiwig-editor v-model="sendAsEmail.body" placeholder="Text content of the email"></wysiwig-editor>
        </div>
        <hr />
        <div class="input-group input-group-sm mb-2">
          <div class="input-group-prepend">
            <div class="input-group-text">Reply To</div>
          </div>
          <select class="form-control" v-model="sendAsEmail.replyTo">
            <option v-for="replyTo in sendAsEmailOptions.replyToAddresses" :value="replyTo.emailAddress" :key="replyTo.emailAddress">
              {{ replyTo.description }}
            </option>
          </select>

          <div class="invalid-feedback">E-Mail "CC" must be one or multiple e-mail addresses (separated with ,)</div>
        </div>
      </form>

      <template v-slot:sidebar>
        <div class="label mt-4">Generate E-Mail</div>

        <b-form-checkbox
          class="mb-1"
          v-model="sendAsEmail.includeChecklists"
          :value="true"
          :unchecked-value="false"
          @input="optionsChanged = true"
        >
          Include checklists
        </b-form-checkbox>

        <b-form-checkbox
          class="mb-1"
          v-model="sendAsEmail.includeComments"
          :value="true"
          :unchecked-value="false"
          @input="optionsChanged = true"
        >
          Include comments
        </b-form-checkbox>

        <b-form-checkbox
          class="mb-1"
          v-model="sendAsEmail.includeLabels"
          :value="true"
          :unchecked-value="false"
          @input="optionsChanged = true"
        >
          Include labels
        </b-form-checkbox>

        <b-form-checkbox
          class="mb-1"
          v-model="includeAttachments"
          :value="true"
          :unchecked-value="false"
          @input="
            includeAttachmentsClicked();
            optionsChanged = true;
            selectedAttachments = [];
          "
        >
          Include attachments
        </b-form-checkbox>

        <small class="mt-2 d-block text-center" v-if="includeAttachments">
          <i class="far fa-exclamation-triangle"></i>
          <br />
          Specify the files, that will be attached to the email:

          <div class="text-left">
            <div v-for="(t, index) in sendableAttachments" :key="index">
              <checkbox-with-label
                :value="attachmentIsSelected(t)"
                :labelStyle="'regular'"
                :label="t.fileName"
                @input="processEmailAttachmentSelection(t)"
              />
            </div>
          </div>
        </small>

        <button
          class="btn btn-block mt-4"
          :class="{ 'btn-success': optionsChanged, 'btn-info': !optionsChanged }"
          id="createChecklist"
          @click="generateText"
        >
          <i class="far fa-cogs icon-left"></i>
          Update text
        </button>

        <div class="label mt-4">Send E-Mail</div>

        <div class="label mb-2" v-b-tooltip.hover.top :title="invalid || !recipientAddresses ? 'Disabled: Please check your input' : ''">
          <button class="btn btn-block btn-sm btn-success" @click="sendEmail()" :disabled="invalid || loading || !recipientAddresses">
            <i class="far fa-paper-plane" v-if="!loading"></i>
            <i class="far fa-spin fa-sync" v-if="loading"></i>
            Send E-Mail
          </button>
        </div>

        <div class="label mb-2">
          <button class="btn btn-block btn-sm btn-secondary" @click="close()">
            <i class="far fa-times"></i>
            Close window
          </button>
        </div>
      </template>
    </modal-window>
  </validation-observer>
</template>

<script lang="ts">
  import Vue from 'vue';
  import Component from 'vue-class-component';
  import { Prop } from 'vue-property-decorator';
  import {
    AttachmentDto,
    BoardItemClient,
    EmailContactDto,
    IBoardItemDto,
    SendAsEmailDto,
    SendAsEmailGetOptionsDto,
    SendAsEmailOptionsDto,
  } from '@/apiclient/apiclient_generated';
  import ModalWindow from '@/components/ModalWindow.vue';
  import { DateTime } from 'luxon';
  import axios from 'axios';
  import { WS_URL as ApiUrl } from '@/config';
  import { GlobalHelper } from '@/helpers/global-helper';
  import { sortBy } from 'lodash';
  import { lookupClient } from '@/apiclient/opsboardapi';
  import WysiwigEditor from '@/components/Misc/WysiwigEditor.vue';
  import { BoardStore } from '@/store/board';
  import CheckboxWithLabel from './Misc/CheckboxWithLabel.vue';

  @Component({
    components: { WysiwigEditor, ModalWindow, 'checkbox-with-label': CheckboxWithLabel },
  })
  export default class SendAsEmail extends Vue {
    @Prop() item: IBoardItemDto;

    private loading = false;
    private boardItemClient = new BoardItemClient(ApiUrl, axios);
    sendAsEmail: SendAsEmailDto = { body: '' } as SendAsEmailDto;

    private receiverMailPattern = /#SER:([^#]*)#/;
    private contentFlagsPattern = /#SERM:([^#]*)#/;
    private descriptionSplitter = '#####';
    private ccReceiverSplitter = '|';
    private optionsChanged = false;
    includeAttachments = false;
    selectedAttachments: AttachmentDto[] = [];

    private sendAsEmailOptions: SendAsEmailOptionsDto = SendAsEmailOptionsDto.fromJS({
      replyToAddresses: [],
      signature: null,
    });

    calculateBody(): string {
      let text = '';

      text += this.getFormattedDescription(this.item.description);
      text += '\n\n';

      if (this.sendAsEmail.includeChecklists && this.item.checklists.length) {
        text += '<strong>CHECKLISTS</strong><br>';

        sortBy(this.item.checklists, 'sortOrder').forEach((checklist) => {
          text += '<strong>' + checklist.title + '</strong>';

          text += '<ul>';
          sortBy(checklist.items, 'sortOrder').forEach((item) => {
            text += `<li> ${item.isCompleted ? '<strong>[DONE]</strong> ' : '[OPEN] '} ${item.title}`;
            if (item.description) {
              text += `<br>${item.description.replace(/\n/g, '<br>')}`;
            }
            text += '<br></li>';
          });
          text += '</ul>';
        });
      }

      if (this.sendAsEmail.includeComments && this.item.comments.filter((_) => !_.isSystemComment).length) {
        text += '<br><strong>COMMENTS</strong>';

        const userComments = this.item.comments.filter((_) => !_.isSystemComment);

        this.lodash.orderBy(userComments, 'createdDate', 'desc').forEach((comment) => {
          text += '<br><em>' + DateTime.fromISO(comment.createdDate).toFormat('dd.MM.yyyy HH:mm') + ' - ' + comment.userTlc + '</em><br>';
          text += comment.text + '<br>';
        });
      }

      if (this.sendAsEmail.includeLabels && this.item.labels?.length) {
        text += '<br><strong>LABELS</strong>';
        text += '<ul>';
        this.item.labels.forEach((label) => {
          text += '<li>' + label.title + '</li>';
        });

        text += '</ul>';
      }

      if (this.includeAttachments && this.selectedAttachments?.length) {
        text += '<br><strong>FILE ATTACHMENTS</strong>';
        text += '<ul>';

        this.selectedAttachments.forEach((attachment) => {
          text += '<li>' + attachment.fileName + '</li>';
        });

        text += '</ul>';
      }

      text += this.sendAsEmailOptions.signature ? '<br>' + this.sendAsEmailOptions.signature : '';

      return text;
    }

    generateText() {
      const body = this.calculateBody();
      this.sendAsEmail.body = body.replace(this.receiverMailPattern, '');
      this.optionsChanged = false;
    }

    attachmentIsSelected(attachment: AttachmentDto): boolean {
      return this.selectedAttachments.map((_) => _.id).indexOf(attachment.id) !== -1;
    }

    async sendEmail() {
      const styleTag =
        '<html><head><style type="text/css"> ' +
        'p { margin:0 !important;} ' +
        "p:empty:before { content: ' '; white-space: pre; }" +
        'td, th { padding: 4px; border-width:1px; border-style:solid; border-color:rgb(171,171,171); box-sizing:border-box; word-break:break-word; white-space:normal;background-color:transparent; }' +
        'table { width: auto;border-collapse: collapse; }' +
        '</style>' +
        '</head>' +
        '<body>@body@' +
        '</body>' +
        '</html>';

      this.loading = true;
      this.sendAsEmail.to = this.recipientAddresses;
      this.sendAsEmail.cc = this.recipientCcAddresses;
      this.sendAsEmail.body = '<div style="font-family:Arial;width: 100%">' + this.sendAsEmail.body + '</div>';
      this.sendAsEmail.body = this.sendAsEmail.body.replace(/<p><\/p>/g, '<br>');
      this.sendAsEmail.body = styleTag.replace('@body@', this.sendAsEmail.body);

      this.sendAsEmail.attachmentIds = this.includeAttachments ? this.selectedAttachments.map((t) => t.id) : [];

      const boardId = BoardStore.currentBoardId;

      const result = await this.boardItemClient.sendAsEmail(boardId, this.item.id, this.sendAsEmail);
      if (result) {
        GlobalHelper.NotifySuccess('E-Mail has been successfully sent.');
        this.close();
      } else {
        GlobalHelper.NotifyError('Error sending E-Mail. See system-comment in BoardItem.');
      }
      this.loading = false;
    }

    close() {
      this.$bvModal.hide('sendAsEmailModal');
    }

    processEmailAttachmentSelection(attachment: AttachmentDto) {
      const index = this.selectedAttachments.findIndex((t) => t.id === attachment.id);

      if (index >= 0) {
        this.selectedAttachments.splice(index, 1);
      } else {
        this.selectedAttachments.push(attachment);
      }

      this.optionsChanged = true;
    }

    async mounted() {
      const getOptionsDto = SendAsEmailGetOptionsDto.fromJS({});
      getOptionsDto.boardItemId = this.item.id;
      getOptionsDto.currentBoardId = BoardStore.currentBoardId;

      this.sendAsEmailOptions = await this.boardItemClient.getSendAsEmailOptions(getOptionsDto);

      this.sendAsEmail = new SendAsEmailDto({
        to: '',
        subject: this.item.title,
        body: this.calculateBody(),
        cc: '',
        replyTo: this.sendAsEmailOptions.replyToAddresses[0]?.emailAddress,
        sender: this.sendAsEmailOptions.sender,
      });

      //pre-populate E-Mail Addresses to send to.
      const addresses = this.item.description?.match(this.receiverMailPattern);

      if (addresses?.length > 1) {
        const matchResult = addresses[1];

        if (matchResult.indexOf(this.ccReceiverSplitter) === -1) {
          const to = matchResult.split(',');
          to.forEach((add) => {
            this.addRecipient(add.trim(), 'to');
          });
        } else {
          const receivers = matchResult.split(this.ccReceiverSplitter);

          const to = receivers[0].split(',');
          to.forEach((add) => {
            this.addRecipient(add.trim(), 'to');
          });

          const cc = receivers[1].split(',');
          cc.forEach((add) => {
            this.addRecipient(add.trim(), 'cc');
          });
        }
      }

      const flags = this.getContentAvailability(this.item.description);

      this.includeAttachments = flags.includeAttachments;
      this.sendAsEmail.includeChecklists = flags.includeChecklists;
      this.sendAsEmail.includeLabels = flags.includeLabels;

      await this.$nextTick();
      this.optionsChanged = false;
    }

    private getFormattedDescription(description: string) {
      if (!description) {
        return '';
      }

      if (description.indexOf(this.descriptionSplitter) === -1) {
        return description;
      }

      const result = this.item.description.split(this.descriptionSplitter);
      return result[0];
    }

    private getContentAvailability(content: string) {
      const contentAvailability = {
        includeAttachments: false,
        includeChecklists: false,
        includeLabels: false,
      };

      if (!content) {
        return contentAvailability;
      }

      const result = this.item.description?.match(this.contentFlagsPattern);

      if (result && result.length) {
        const flags = result[1].split('|');

        contentAvailability.includeAttachments = flags.some((_) => _.toLowerCase() === 'a');
        contentAvailability.includeChecklists = flags.some((_) => _.toLowerCase() === 'c');
        contentAvailability.includeLabels = flags.some((_) => _.toLowerCase() === 'l');
      }

      return contentAvailability;
    }

    private chosenRecipients: EmailContactDto[] = [];
    private chosenRecipientsCc: EmailContactDto[] = [];
    private options: EmailContactDto[] = [];

    addRecipient(tag, type) {
      const regEx = /^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;
      if (!regEx.test(tag)) {
        alert(`${tag} is no valid E-Mail address.`);
        return;
      }

      const recipient = new EmailContactDto();
      recipient.email = tag;
      recipient.name = tag;

      switch (type) {
        case 'to':
          this.chosenRecipients.push(recipient);
          break;
        case 'cc':
          this.chosenRecipientsCc.push(recipient);
          break;
      }
    }

    async searchReceivers(query: string) {
      const result = await lookupClient.getEmailContacts(BoardStore.currentBoardId, query);
      this.options = result;
    }

    get recipientAddresses(): string {
      return this.chosenRecipients.map((_) => (_.email as any).replaceAll(';', ',')).join(',');
    }

    get recipientCcAddresses(): string {
      return this.chosenRecipientsCc.map((_) => (_.email as any).replaceAll(';', ',')).join(',');
    }

    get sendableAttachments(): AttachmentDto[] {
      return this.item.attachments.filter(_=>_.fileExtension != '.eml');
    }

    async includeAttachmentsClicked() {
      await this.$nextTick();
      if (this.includeAttachments) {
        for (let i = 0; i < this.sendableAttachments.length; i++) {
          this.selectedAttachments.push(this.sendableAttachments[i]);
        }
      } else {
        this.selectedAttachments = [];
      }
    }
  }
</script>

<style scoped lang="scss"></style>

<style lang="scss">
  .custom-control.custom-checkbox {
    label {
      padding-top: 2px !important;
    }
  }
</style>